import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API = import.meta.env.VITE_BACKEND_URL;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("userToken")}`,
    'Content-Type': 'application/json',
});

// Retrieve user info from localStorage
const userFromStorage = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;

// Guest ID
const initialGuestId =
    localStorage.getItem("guestId") || `guest_${new Date().getTime()}`;
localStorage.setItem("guestId", initialGuestId);

// ─────────────────────────────────────────────────────────────
// Thunks
// ─────────────────────────────────────────────────────────────

export const loginUser = createAsyncThunk("auth/loginUser", async (userData, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API}/api/users/login`, userData);
        localStorage.setItem("userInfo", JSON.stringify(response.data.user));
        localStorage.setItem("userToken", response.data.token);
        return response.data.user;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Login failed" });
    }
});

export const registerUser = createAsyncThunk("auth/registerUser", async (userData, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API}/api/users/register`, userData);
        localStorage.setItem("userInfo", JSON.stringify(response.data.user));
        localStorage.setItem("userToken", response.data.token);
        return response.data.user;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Registration failed" });
    }
});

// Fetches fresh profile from backend (phone + savedAddresses)
export const fetchUserProfile = createAsyncThunk("auth/fetchUserProfile", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(`${API}/api/users/profile`, {
            headers: authHeaders(),
        });
        localStorage.setItem("userInfo", JSON.stringify(response.data));
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to fetch profile" });
    }
});

// Updates phone (or name) on the backend profile
export const updateUserProfile = createAsyncThunk("auth/updateUserProfile", async (data, { rejectWithValue }) => {
    try {
        const response = await axios.put(`${API}/api/users/profile`, data, {
            headers: authHeaders(),
        });
        localStorage.setItem("userInfo", JSON.stringify(response.data));
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to update profile" });
    }
});

// Saves a new delivery address to the user's profile
export const saveUserAddress = createAsyncThunk("auth/saveUserAddress", async (addressData, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API}/api/users/addresses`, addressData, {
            headers: authHeaders(),
        });
        localStorage.setItem("userInfo", JSON.stringify(response.data));
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to save address" });
    }
});

// Removes a saved address by its MongoDB _id
export const deleteUserAddress = createAsyncThunk("auth/deleteUserAddress", async (addressId, { rejectWithValue }) => {
    try {
        const response = await axios.delete(`${API}/api/users/addresses/${addressId}`, {
            headers: authHeaders(),
        });
        localStorage.setItem("userInfo", JSON.stringify(response.data));
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to remove address" });
    }
});

// ─────────────────────────────────────────────────────────────
// Soft-delete the authenticated user's account.
//
// On success the backend returns { message, scheduledDeletionAt }.
// The thunk then triggers a full client-side logout — localStorage
// is cleared and Redux state is reset — so the user is immediately
// signed out and cannot interact with the app.
//
// Orders, transactions, and invoices are preserved on the backend
// per data retention policy. Only the user document's PII is wiped.
// ─────────────────────────────────────────────────────────────
export const deleteAccount = createAsyncThunk("auth/deleteAccount", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.delete(`${API}/api/users/account`, {
            headers: authHeaders(),
            // confirmText validated on the backend; we pass it in the request body.
            // axios DELETE with a body requires the `data` key:
            data: { confirmText: 'DELETE' },
        });
        return response.data; // { message, scheduledDeletionAt }
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to delete account" });
    }
});

// ─────────────────────────────────────────────────────────────
// Recover a soft-deleted account within the 30-day window.
//
// The user supplies email + password to prove ownership.
// On success the backend restores the account and issues a fresh
// JWT — we store it exactly like a normal login so the user is
// auto-signed-in and redirected to their profile.
// ─────────────────────────────────────────────────────────────
export const recoverAccount = createAsyncThunk("auth/recoverAccount", async (credentials, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API}/api/users/recover-account`, credentials);
        localStorage.setItem("userInfo",  JSON.stringify(response.data.user));
        localStorage.setItem("userToken", response.data.token);
        return response.data.user;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Account recovery failed" });
    }
});

// ─────────────────────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────────────────────

const authSlice = createSlice({
    name: "auth",
    initialState: {
        user:                userFromStorage,
        guestId:             initialGuestId,
        loading:             false,
        profileLoading:      false,
        deleteLoading:       false,
        recoverLoading:      false,
        error:               null,
        // Populated when login returns 403 isDeleted:true so the login
        // page can show "Recover Account" with the scheduled deletion date.
        deletedAccountInfo:  null,
    },
    reducers: {
        logout: (state) => {
            state.user               = null;
            state.error              = null;
            state.deletedAccountInfo = null;
            state.guestId            = `guest_${new Date().getTime()}`;
            localStorage.removeItem("userInfo");
            localStorage.removeItem("userToken");
            localStorage.setItem("guestId", state.guestId);
        },
        generateNewGuestId: (state) => {
            state.guestId = `guest_${new Date().getTime()}`;
            localStorage.setItem("guestId", state.guestId);
        },
        clearError: (state) => {
            state.error = null;
        },
        clearDeletedAccountInfo: (state) => {
            state.deletedAccountInfo = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ── Login ──────────────────────────────────────────────────
            .addCase(loginUser.pending,   (state) => { state.loading = true; state.error = null; state.deletedAccountInfo = null; })
            .addCase(loginUser.fulfilled, (state, action) => { state.loading = false; state.user = action.payload; })
            .addCase(loginUser.rejected,  (state, action) => {
                state.loading = false;
                state.error   = action.payload?.message || "Login failed";
                // Backend returns isDeleted:true when a deleted account tries to log in.
                // Store the info so the login page can surface a recovery prompt.
                if (action.payload?.isDeleted) {
                    state.deletedAccountInfo = {
                        scheduledDeletionAt: action.payload.scheduledDeletionAt,
                    };
                }
            })

            // ── Register ───────────────────────────────────────────────
            .addCase(registerUser.pending,   (state) => { state.loading = true;  state.error = null; })
            .addCase(registerUser.fulfilled, (state, action) => { state.loading = false; state.user = action.payload; })
            .addCase(registerUser.rejected,  (state, action) => { state.loading = false; state.error = action.payload?.message || "Registration failed"; })

            // ── Fetch Profile ──────────────────────────────────────────
            .addCase(fetchUserProfile.pending,   (state) => { state.profileLoading = true; })
            .addCase(fetchUserProfile.fulfilled, (state, action) => {
                state.profileLoading = false;
                state.user = { ...state.user, ...action.payload };
            })
            .addCase(fetchUserProfile.rejected,  (state) => { state.profileLoading = false; })

            // ── Update Profile ─────────────────────────────────────────
            .addCase(updateUserProfile.fulfilled, (state, action) => {
                state.user = { ...state.user, ...action.payload };
            })

            // ── Save Address ───────────────────────────────────────────
            .addCase(saveUserAddress.fulfilled, (state, action) => {
                state.user = { ...state.user, ...action.payload };
            })

            // ── Delete Address ─────────────────────────────────────────
            .addCase(deleteUserAddress.fulfilled, (state, action) => {
                state.user = { ...state.user, ...action.payload };
            })

            // ── Delete Account ─────────────────────────────────────────
            // pending / rejected are handled locally in the component.
            // On fulfilled: wipe all client state exactly like logout.
            .addCase(deleteAccount.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
            })
            .addCase(deleteAccount.fulfilled, (state) => {
                state.deleteLoading = false;
                // Full client-side sign-out
                state.user               = null;
                state.error              = null;
                state.deletedAccountInfo = null;
                state.guestId            = `guest_${new Date().getTime()}`;
                localStorage.removeItem("userInfo");
                localStorage.removeItem("userToken");
                localStorage.setItem("guestId", state.guestId);
            })
            .addCase(deleteAccount.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload?.message || "Failed to delete account";
            })

            // ── Recover Account ────────────────────────────────────────
            .addCase(recoverAccount.pending, (state) => {
                state.recoverLoading = true;
                state.error = null;
            })
            .addCase(recoverAccount.fulfilled, (state, action) => {
                state.recoverLoading     = false;
                state.user               = action.payload;
                state.deletedAccountInfo = null;
                state.error              = null;
            })
            .addCase(recoverAccount.rejected, (state, action) => {
                state.recoverLoading = false;
                state.error = action.payload?.message || "Account recovery failed";
            });
    },
});

export const { logout, generateNewGuestId, clearError, clearDeletedAccountInfo } = authSlice.actions;
export default authSlice.reducer;
